import React from 'react';
import { X, Calendar, User, Hotel, DollarSign, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface BookingDetails {
  userName: string;
  userEmail: string;
  roomName: string;
  roomImage: string;
  checkIn: string;
  checkOut: string;
  totalPrice: number;
  nights: number;
  pricePerNight: number;
  source?: string;
  isExternal?: boolean;
}

interface BookingConfirmationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  bookingDetails: BookingDetails;
  isSubmitting: boolean;
}

const BookingConfirmationPopup: React.FC<BookingConfirmationPopupProps> = ({
  isOpen,
  onClose,
  onConfirm,
  bookingDetails,
  isSubmitting
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Confirm Your Booking</h3>
                <p className="text-sm text-gray-500">Please review the details before confirming</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-500 focus:outline-none disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Room Image and Basic Info */}
          <div className="mb-6">
            <div className="flex items-start space-x-4">
              <img
                src={bookingDetails.roomImage}
                alt={bookingDetails.roomName}
                className="h-20 w-20 object-cover rounded-lg flex-shrink-0"
              />
              <div className="flex-1">
                <h4 className="text-lg font-medium text-gray-900">{bookingDetails.roomName}</h4>
                <div className="mt-1 flex items-center text-sm text-gray-500">
                  <DollarSign className="h-4 w-4 mr-1" />
                  <span>${bookingDetails.pricePerNight} per night</span>
                </div>
                {bookingDetails.source && (
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {bookingDetails.source}
                      {bookingDetails.isExternal && ' (External)'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Booking Summary</h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <User className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Guest Name</span>
                </div>
                <span className="font-medium text-gray-900">{bookingDetails.userName}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <User className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Email</span>
                </div>
                <span className="font-medium text-gray-900">{bookingDetails.userEmail}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Check-in</span>
                </div>
                <span className="font-medium text-gray-900">
                  {format(parseISO(bookingDetails.checkIn), 'MMM d, yyyy')} at 12:00 PM
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Check-out</span>
                </div>
                <span className="font-medium text-gray-900">
                  {format(parseISO(bookingDetails.checkOut), 'MMM d, yyyy')} at 12:00 PM
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Duration</span>
                </div>
                <span className="font-medium text-gray-900">
                  {bookingDetails.nights} night{bookingDetails.nights !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-indigo-50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Price Breakdown</h4>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  ${bookingDetails.pricePerNight} × {bookingDetails.nights} night{bookingDetails.nights !== 1 ? 's' : ''}
                </span>
                <span className="text-gray-900">${bookingDetails.totalPrice}</span>
              </div>
              
              <div className="border-t border-indigo-200 pt-2">
                <div className="flex justify-between">
                  <span className="text-base font-medium text-gray-900">Total Amount</span>
                  <span className="text-xl font-bold text-indigo-600">${bookingDetails.totalPrice}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isSubmitting}
              className="flex-1 bg-indigo-600 text-white px-4 py-3 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Booking
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationPopup;